import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

const translations = {
  es: {
    // Navigation
    home: 'Inicio',
    catalog: 'Catálogo',
    featured: 'Destacados',
    contact: 'Contacto',
    cart: 'Carrito',
    
    // Hero
    heroTitle: 'Tecnología que transforma tu espacio',
    heroSubtitle: 'Simple. Potente. Disponible hoy.',
    viewCatalog: 'Ver catálogo',
    buyNow: 'Comprar ahora',
    
    // Categories
    all: 'Todos',
    tvs: 'Televisores',
    airConditioners: 'Aires Acondicionados',
    appliances: 'Electrodomésticos',
    fans: 'Abanicos',
    audio: 'Audio',
    
    // Product
    addToCart: 'Agregar al carrito',
    viewDetails: 'Ver detalles',
    features: 'Características',
    warranty: 'Garantía',
    description: 'Descripción',
    backToCatalog: 'Volver al catálogo',
    share: 'Compartir',
    available: 'Disponible',
    unavailable: 'No disponible',
    
    // Financing
    from: 'Desde',
    perMonth: '/mes',
    takeItToday: 'Llévatelo hoy',
    payComfortably: 'Paga cómodo mes a mes',
    financingSubject: 'Financiamiento sujeto a evaluación',
    consultWhatsApp: 'Consulta por WhatsApp',
    
    // Cart
    yourCart: 'Tu carrito',
    emptyCart: 'Tu carrito está vacío',
    continueShopping: 'Seguir comprando',
    quantity: 'Cantidad',
    price: 'Precio',
    subtotal: 'Subtotal',
    total: 'Total',
    proceedCheckout: 'Proceder al pago',
    remove: 'Eliminar',
    
    // Checkout
    completeOrder: 'Completar pedido',
    yourInfo: 'Tus datos',
    fullName: 'Nombre completo',
    phone: 'Teléfono',
    city: 'Ciudad',
    orderSummary: 'Resumen del pedido',
    sendWhatsApp: 'Enviar por WhatsApp',
    
    // Services
    services: 'Servicios',
    deliveryAvailable: 'Entrega disponible',
    professionalInstallation: 'Instalación profesional disponible',
    installationCost: 'Costo adicional',
    warrantyIncluded: 'Garantía incluida según tipo de producto',
    delivery: 'Entrega',
    installation: 'Instalación',
    
    // Footer
    aboutUs: 'Sobre nosotros',
    contactInfo: 'Información de contacto',
    followUs: 'Síguenos',
    allRightsReserved: 'Todos los derechos reservados',
    
    // Best Sellers
    bestSellers: 'Más vendidos',
    
    // Comparator
    compare: 'Comparar',
    compareModels: 'Comparar modelos',
    selectToCompare: 'Selecciona productos para comparar',
    selected: 'Seleccionado',
    compareNow: 'Comparar ahora',
    size: 'Tamaño',
    brand: 'Marca',
    resolution: 'Resolución',
    panel: 'Panel',
    system: 'Sistema',
    type: 'Tipo',
    power: 'Potencia',
    capacity: 'Capacidad',
    speeds: 'Velocidades',
    material: 'Material',
    coverage: 'Área',
    consumption: 'Consumo',
    connectivity: 'Conectividad',
    financing: 'Financiamiento',
    
    // Search
    search: 'Buscar',
    searchPlaceholder: 'Buscar productos...',
    recommendations: 'Recomendaciones',
    categories: 'Categorías',
    
    // Admin
    adminPanel: 'Panel de administración',
    products: 'Productos',
    orders: 'Pedidos',
    settings: 'Configuración',
    
    // Loading
    loading: 'Cargando...',
    loadingProduct: 'Cargando producto...',
    
    // General
    exploreCollection: 'Explora toda la colección',
    discoverMore: 'Descubre más'
  },
  en: {
    // Navigation
    home: 'Home',
    catalog: 'Catalog',
    featured: 'Featured',
    contact: 'Contact',
    cart: 'Cart',
    
    // Hero
    heroTitle: 'Technology that transforms your space',
    heroSubtitle: 'Simple. Powerful. Available today.',
    viewCatalog: 'View catalog',
    buyNow: 'Buy now',
    
    // Categories
    all: 'All',
    tvs: 'TVs',
    airConditioners: 'Air Conditioners',
    appliances: 'Appliances',
    fans: 'Fans',
    audio: 'Audio',
    
    // Product
    addToCart: 'Add to cart',
    viewDetails: 'View details',
    features: 'Features',
    warranty: 'Warranty',
    description: 'Description',
    backToCatalog: 'Back to catalog',
    share: 'Share',
    available: 'Available',
    unavailable: 'Unavailable',
    
    // Financing
    from: 'From',
    perMonth: '/month',
    takeItToday: 'Take it home today',
    payComfortably: 'Pay comfortably month by month',
    financingSubject: 'Financing subject to approval',
    consultWhatsApp: 'Consult via WhatsApp',
    
    // Cart
    yourCart: 'Your cart',
    emptyCart: 'Your cart is empty',
    continueShopping: 'Continue shopping',
    quantity: 'Quantity',
    price: 'Price',
    subtotal: 'Subtotal',
    total: 'Total',
    proceedCheckout: 'Proceed to checkout',
    remove: 'Remove',
    
    // Checkout
    completeOrder: 'Complete order',
    yourInfo: 'Your information',
    fullName: 'Full name',
    phone: 'Phone',
    city: 'City',
    orderSummary: 'Order summary',
    sendWhatsApp: 'Send via WhatsApp',
    
    // Services
    services: 'Services',
    deliveryAvailable: 'Delivery available',
    professionalInstallation: 'Professional installation available',
    installationCost: 'Additional cost',
    warrantyIncluded: 'Warranty included according to product type',
    delivery: 'Delivery',
    installation: 'Installation',
    
    // Footer
    aboutUs: 'About us',
    contactInfo: 'Contact information',
    followUs: 'Follow us',
    allRightsReserved: 'All rights reserved',
    
    // Best Sellers
    bestSellers: 'Best Sellers',
    
    // Comparator
    compare: 'Compare',
    compareModels: 'Compare models',
    selectToCompare: 'Select products to compare',
    selected: 'Selected',
    compareNow: 'Compare now',
    size: 'Size',
    brand: 'Brand',
    resolution: 'Resolution',
    panel: 'Panel',
    system: 'System',
    type: 'Type',
    power: 'Power',
    capacity: 'Capacity',
    speeds: 'Speeds',
    material: 'Material',
    coverage: 'Coverage',
    consumption: 'Consumption',
    connectivity: 'Connectivity',
    financing: 'Financing',
    
    // Search
    search: 'Search',
    searchPlaceholder: 'Search products...',
    recommendations: 'Recommendations',
    categories: 'Categories',
    
    // Admin
    adminPanel: 'Admin Panel',
    products: 'Products',
    orders: 'Orders',
    settings: 'Settings',
    
    // Loading
    loading: 'Loading...',
    loadingProduct: 'Loading product...',
    
    // General
    exploreCollection: 'Explore the full collection',
    discoverMore: 'Discover more'
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('es');

  useEffect(() => {
    const savedLanguage = localStorage.getItem('nagoc_language') || 'es';
    setLanguage(savedLanguage);
  }, []);

  const toggleLanguage = () => {
    const newLanguage = language === 'es' ? 'en' : 'es';
    setLanguage(newLanguage);
    localStorage.setItem('nagoc_language', newLanguage);
  };

  const t = (key) => {
    return translations[language][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};
